/**
 * 제자리에 그리되 「새 창으로 분리」 가 되는 화면(2026-10-19 사용자 요청) — 쿼리 챌린지.
 *
 * **모양은 창(AppModal)과 같다** — 같은 틀 클래스(app-modal · app-modal-header · app-modal-title · app-modal-close, styles/appModal.css)를 쓴다.
 * 작업 탭의 화면(튜닝 사례 등)과 제목줄 높이·글자·아이콘·닫기가 달라 보이던 것을 맞췄다(2026-10-20 사용자 요청: 「같은 화면 레이아웃 컴포넌트」).
 * 동작은 실시간 대시보드 같은 창과 **같은 장치**(useModalWindow): 화면은 늘 같은 포털 그릇에 그리고, 분리하면 그 그릇의 DOM 노드만
 * 새 창으로 옮긴다 — 그래서 분리·되돌리기에 입력·조회 결과가 남는다. 전체 화면은 분리한 뒤 그 창에서만 켜진다(inline).
 */
import { useEffect, useId } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FiX } from 'react-icons/fi';
import useModalWindow from './useModalWindow';
import './DetachableView.css';

export default function DetachableView({ title, icon, onClose, headerExtra, className, children, onPoppedChange }) {
    const { t } = useTranslation();
    const titleId = useId();
    // dock:false — 작업 탭으로 올리지 않는다(편집기 칸 안의 화면이다). inline — 제자리라 이동·앱 안 최대화가 없다
    const win = useModalWindow({ title, dock: false, inline: true });
    // 분리한 동안에는 그 자리가 비므로, 부르는 쪽이 원래 화면(편집기 등)을 다시 보이게 한다
    const popped = win.popped;
    useEffect(() => { if (onPoppedChange) onPoppedChange(popped); }, [popped, onPoppedChange]);
    useEffect(() => () => { if (onPoppedChange) onPoppedChange(false); }, [onPoppedChange]);
    const closeLabel = t('common:ui.close');
    return win.wrap(
        <div ref={win.frameRef} data-app-modal role="region" aria-labelledby={titleId}
            className={`app-modal detach-view${popped ? ' popped' : ''}${className ? ` ${className}` : ''}`}
            style={popped ? win.frameStyle : undefined}>
            <div className="app-modal-header">
                <h2 id={titleId} className="app-modal-title">{icon}{title}</h2>
                <div className="app-modal-header-right">
                    {headerExtra}
                    {win.controls}
                    {onClose && (
                        <button type="button" className="app-modal-close" onClick={onClose} aria-label={closeLabel} title={closeLabel}>
                            <FiX size={16} />
                        </button>
                    )}
                </div>
            </div>
            <div className="detach-view-body">{children}</div>
        </div>
    );
}

DetachableView.propTypes = {
    /** 제목 — 메뉴·단추와 같은 번역 문구(분리한 창의 제목도 된다) */
    title: PropTypes.string.isRequired,
    /** 메뉴와 같은 아이콘, 14px */
    icon: PropTypes.node,
    onClose: PropTypes.func,
    /** 제목줄 오른쪽, 창 조작 단추 앞 */
    headerExtra: PropTypes.node,
    className: PropTypes.string,
    children: PropTypes.node,
    /** (popped: boolean) — 새 창으로 분리됐는지. 고정 함수를 줄 것(useCallback) */
    onPoppedChange: PropTypes.func,
};
